/**
 * Unified-filter model — pure builder that turns the store's filter state
 * (`activeSpotlights` + `columnFilters`) into a flat list of chip descriptors,
 * one per active filter of any kind. The unified chip rail renders these; the
 * logic lives here (framework-free) so it's directly unit-testable and shared
 * by the SearchResults and Projects rails.
 *
 * Chip kinds: `spotlight` (a preset), `numeric` (a range), `controlled` (a
 * checkbox set, e.g. Situation), `location` (countries + optional city), and
 * `keyword` (one combined chip per keyword-cloud dimension, carrying its
 * All/Any `mode`).
 */

import {
  COLUMN_FILTER_TYPES,
  COLUMN_FILTER_TYPE_BY_KEY,
  COLUMN_LABEL_BY_KEY,
  formatNumericForChip
} from './columnFilterTypes';
import { SPOTLIGHT_DEFINITIONS } from './spotlightDefinitions';

/**
 * Format a numeric range for a chip label. Reuses the tested per-column numeric
 * formatter (unit + display-scale aware). Open-ended ranges read as `≥ x` / `≤ y`.
 * Returns '' for an empty range so the caller can skip it.
 */
export function formatRangeForChip(key, range) {
  if (!range) return '';
  const { min, max } = range;
  const hasMin = min != null;
  const hasMax = max != null;
  if (hasMin && hasMax) {
    return `${formatNumericForChip(key, min)} – ${formatNumericForChip(key, max)}`;
  }
  if (hasMin) return `≥ ${formatNumericForChip(key, min)}`;
  if (hasMax) return `≤ ${formatNumericForChip(key, max)}`;
  return '';
}

function labelFor(key) {
  return COLUMN_LABEL_BY_KEY[key] ?? key;
}

/**
 * Column keys owned by a currently-active spotlight (via `relatedColumn`).
 * The unified rail suppresses those column chips so a spotlight-synced cut
 * (e.g. Top-quartile EBIT → an `ebitPercentage` min) doesn't render twice —
 * once as the preset chip and once as the synced column chip.
 *
 * Suppression is by ownership (the spotlight is active AND owns the column), not
 * by value-equality, so it stays robust when the loaded set shifts the synced
 * threshold. Caveat: if the analyst hand-edits an owned column while its
 * spotlight is active, that column's chip stays hidden until the preset is
 * removed — acceptable for v1.
 */
function spotlightOwnedColumnKeys(activeSpotlights, spotlightDefs) {
  const owned = new Set();
  for (const def of spotlightDefs) {
    if (activeSpotlights?.has?.(def.id) && def.relatedColumn?.columnKey) {
      owned.add(def.relatedColumn.columnKey);
    }
  }
  return owned;
}

/**
 * Build the flat list of active-filter chip descriptors.
 *
 * @param {Set<string>} activeSpotlights
 * @param {Object} columnFilters - `{ [columnKey]: filterValue }`
 * @param {Object} [opts]
 * @param {ReadonlyArray} [opts.spotlightDefs=SPOTLIGHT_DEFINITIONS]
 * @param {Set<string>|null} [opts.categoryExcludedSpotlightIds=null] - spotlight
 *   ids hidden for the current search category (e.g. `high-match` on Industries)
 *   — excluded so a stale hidden id can't surface, mirroring `filteredResults`.
 * @returns {Array<{kind,key,label,valueText?,values?,mode?}>}
 */
export function buildActiveFilterChips(
  activeSpotlights,
  columnFilters,
  { spotlightDefs = SPOTLIGHT_DEFINITIONS, categoryExcludedSpotlightIds = null } = {}
) {
  const chips = [];

  // 1) Active spotlight preset chips (skip category-hidden ids).
  for (const def of spotlightDefs) {
    if (activeSpotlights?.has?.(def.id) && !categoryExcludedSpotlightIds?.has?.(def.id)) {
      chips.push({ kind: 'spotlight', key: def.id, label: def.label });
    }
  }

  // 2) One chip per active column filter, suppressing spotlight-owned columns.
  const owned = spotlightOwnedColumnKeys(activeSpotlights, spotlightDefs);
  for (const key of Object.keys(columnFilters ?? {})) {
    if (owned.has(key) || !columnFilters[key]) continue;
    const chip = columnChip(key, columnFilters[key]);
    if (chip) chips.push(chip);
  }

  return chips;
}

/**
 * Build the chip descriptor for one active column filter, or null when the
 * value is effectively empty. Split out of buildActiveFilterChips (guard-clause
 * style) so each function's branching stays shallow.
 */
function columnChip(key, value) {
  const label = labelFor(key);
  switch (COLUMN_FILTER_TYPE_BY_KEY[key]) {
    case COLUMN_FILTER_TYPES.NUMERIC: {
      const valueText = formatRangeForChip(key, value.range);
      return valueText ? { kind: 'numeric', key, label, valueText } : null;
    }
    case COLUMN_FILTER_TYPES.KEYWORD_CLOUD: {
      const values = [...(value.keywords ?? [])];
      if (!values.length) return null;
      return {
        kind: 'keyword',
        key,
        label,
        values,
        mode: value.mode || 'or',
        valueText: values.length <= 2 ? values.join(', ') : `${values.length} keywords`
      };
    }
    case COLUMN_FILTER_TYPES.LOCATION: {
      const countries = [...(value.values ?? [])];
      const parts = [];
      if (countries.length) parts.push(countries.join(', '));
      if (value.text) parts.push(`"${value.text}"`);
      const valueText = parts.join(' · ');
      return valueText ? { kind: 'location', key, label, values: countries, valueText } : null;
    }
    case COLUMN_FILTER_TYPES.CONTROLLED: {
      const values = [...(value.values ?? [])];
      return values.length ? { kind: 'controlled', key, label, values, valueText: values.join(', ') } : null;
    }
    default:
      return null;
  }
}
